import { useEffect, useRef, useState } from 'react';
import { useMountEffect } from '@s20/hooks/state.hooks';

// Adapted from https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useIntersectionObserver/useIntersectionObserver.ts
export const useIntersectionObserver = (options) => {
    const {
        threshold = 0,
        root = null,
        rootMargin = '0%',
        freezeOnceVisible = false,
        initialIsIntersecting = false,
        onChange,
    } = options || {};

    const [ref, setRef] = useState(null);

    const [state, setState] = useState(() => ({
        isIntersecting: initialIsIntersecting,
        entry: undefined,
    }));

    const callbackRef = useRef(null);

    callbackRef.current = onChange;

    const frozen = state.entry?.isIntersecting && freezeOnceVisible;

    useEffect(() => {
        if (
            !ref
            || !('IntersectionObserver' in window)
            || frozen
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const thresholds = Array.isArray(observer.thresholds)
                    ? observer.thresholds
                    : [observer.thresholds];

                entries.forEach((entry) => {
                    const isIntersecting = entry.isIntersecting && thresholds.some((observerThreshold) => entry.intersectionRatio >= observerThreshold);

                    setState({ isIntersecting, entry });

                    if (callbackRef.current) {
                        callbackRef.current(isIntersecting, entry);
                    }
                });
            },
            { threshold, root, rootMargin },
        );

        observer.observe(ref);

        return () => {
            observer.disconnect();
        };
    }, [ref, root, rootMargin, frozen, freezeOnceVisible, threshold ]);

    const prevRef = useRef(null);

    useMountEffect(() => {
        if (!ref && state.entry?.target && !freezeOnceVisible && !frozen && prevRef.current !== state.entry.target) {
            prevRef.current = state.entry.target;
            setState({ isIntersecting: initialIsIntersecting, entry: undefined });
        }
    }, [ref, state.entry, freezeOnceVisible, frozen, initialIsIntersecting]);

    return {
        ref: setRef,
        isIntersecting: !!state.isIntersecting,
        entry: state.entry,
    };
};